import { put, call, takeEvery, select } from "redux-saga/effects";

import * as apis from "../../network/apis/events";
import * as types from "./types";
import * as actions from "./actions";
import { showErrorMsg, showSuccessMsg } from "../../utils/Helpers";
import {
  setCreateEventFormStep,
  setCreatedEventId,
  setActivePublishMode,
} from "../Routing/actions";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import history from "../../routes/History";
import { eventStatuses } from "../../utils/Constants";
import messages from "../../assets/locale/messages";
import { isSupervisorManger } from "../../utils/Permissions";

let lastEventFetch = { id: null, timestamp: 0 };
const DEBOUNCE_TIME = 100;

/**
 * Show user-facing error for event API failures.
 * Displays "No Internet Connection" when the request never reached the server.
 */
function* showEventError(err) {
  const lang = yield select((state) => state.locale.lang);
  const message = !err.response
    ? messages[lang].shared.noInternetConnection
    : err.response?.data?.message || messages[lang].shared.generalErrMsg;
  yield showErrorMsg(message);
}

function* getEventsListSaga({ payload }) {
  try {
    const listApi = isSupervisorManger()
      ? apis.getUserAppEventsList
      : apis.getEventsList;
    const response = yield call(listApi, payload);
    yield put(
      actions.getEventsResponse({
        list: response.data.data,
        meta: {
          currentPage: +response.headers["current-page"],
          count: +response.headers["total-count"],
          pages: +response.headers["total-pages"],
        },
      })
    );
    if (+response.headers["current-page"] > +response.headers["total-pages"]) {
      yield put(
        actions.getEventsRequest({
          ...payload,
          page: +response.headers["total-pages"],
        })
      );
    }
  } catch (error) {
    yield showEventError(error);
  }
}

function* getEventSaga({ payload }) {
  try {
    const now = Date.now();
    const eventId = payload.toString();
    
    if (lastEventFetch.id === eventId && (now - lastEventFetch.timestamp) < DEBOUNCE_TIME) {
      return;
    }

    lastEventFetch = { id: eventId, timestamp: now };

    const getEventApi = isSupervisorManger()
      ? apis.getUserAppEvent
      : apis.getEvent;
    const response = yield call(getEventApi, payload);
    yield put(actions.getEventResponse(response.data.data));
  } catch (err) {
    yield put(actions.getEventResponse(null));
    yield showEventError(err);
    const status = err.response?.status;
    // Missing event or no access — leave the empty view shell and go back to the list.
    if (status === 404 || status === 403) {
      history.replace(ROUTE_PATHS.events);
    }
  }
}

function* addEventSaga({ payload }) {
  try {
    const response = yield call(apis.addEvent, payload);
    yield put(setCreatedEventId(response?.data?.data?.id));
    yield put(
      actions.createdEventID({ createdEventId: response?.data?.data?.id })
    );
    if (payload.nextStep === "events") {
      history.push({
        pathname: ROUTE_PATHS["events"],
        state: eventStatuses.draft,
      });
    } else {
      yield put(setCreateEventFormStep(payload.nextStep));
    }
  } catch (err) {
    yield showEventError(err);
  }
}

function* editEventSaga({ payload }) {
  try {
    let response;
    if (payload.validate) {
      const {
        id,
        data: {
          building_start_at,
          dismantling_end_at,
          event_start_at,
          event_end_at,
        },
      } = payload;
      response = yield call(apis.checkEventConflicts, {
        id,
        params: {
          building_start_at,
          dismantling_end_at,
          event_start_at,
          event_end_at,
        },
      });
      if (response?.data?.number_of_conflicting_resources > 0) {
        yield put(
          actions.showEventConflictWarningAlert({
            show: true,
            message: response.data.message,
            count: response.data.number_of_conflicting_resources,
          })
        );
      }
    }
    if (
      !payload.validate ||
      response?.data?.number_of_conflicting_resources === 0
    ) {
      yield call(apis.editEvent, payload);
      yield put(
        actions.showEventConflictWarningAlert({
          show: false,
          message: "",
          count: 0,
        })
      );
      if (payload.nextStep === "events") {
        history.push({
          pathname: ROUTE_PATHS["events"],
          state: eventStatuses.draft,
        });
      } else if (payload.nextStep === "view-event") {
        history.push(ROUTE_PATHS["viewEvent"].replace(":id", payload.id));
      } else {
        yield put(setCreateEventFormStep(payload.nextStep));
      }
    }
  } catch (err) {
    yield showEventError(err);
  }
}

function* publishEventSaga({ payload }) {
  try {
    const response = yield call(apis.editEvent, payload);
    yield call(apis.publishEvent, response?.data?.data?.id);
    history.push({
      pathname: ROUTE_PATHS["events"],
      state: eventStatuses.upcoming,
    });
  } catch (err) {
    if (err.response?.config?.method === "post") {
      yield put(setCreateEventFormStep(0));
      yield put(setActivePublishMode(true));
      yield put(actions.setInitialPublishValidationStep(true));
    }
    yield showEventError(err);
  }
}

function* deleteEventSaga({ payload }) {
  try {
    yield call(apis.deleteEvent, payload);
    yield showSuccessMsg("eventDeletedSuccessfully");
    history.push(ROUTE_PATHS["events"]);
  } catch (err) {
    yield showEventError(err);
  }
}

function* cancelEventSaga({ payload }) {
  try {
    yield call(apis.cancelEvent, payload);
    yield showSuccessMsg("eventCanceledSuccessfully");
    history.push({
      pathname: ROUTE_PATHS["events"],
      state: eventStatuses.cancelled,
    });
  } catch (err) {
    yield showEventError(err);
  }
}

function* inviteApplicantsSaga({ payload }) {
  try {
    const response = yield call(apis.inviteApplicants, payload);
    yield showSuccessMsg(response.data.message, true);
    history.push(ROUTE_PATHS.viewEvent.replace(":id", payload.event_id));
  } catch (err) {
    yield showEventError(err);
  }
}

function* sendNotificationRequestSage({ payload }) {
  try {
    const response = yield call(apis.sendPushNotification, payload);
    yield showSuccessMsg(response.data.message, true);
  } catch (err) {
    yield showEventError(err);
  }
}

function* addBulkRewardToggleRequestSaga({ payload }) {
  try {
    const response = yield call(apis.addBulkRewardToggle, payload);
    yield showSuccessMsg(response.data.message, true);
  } catch (err) {
    yield showEventError(err);
  }
}

function* getPayrollPerDaySaga({ payload }) {
  try {
    const response = yield call(apis.getPayrollPerDay, payload);
    yield put(
      actions.getPayrollPerDayResponse({
        payrollDay: response.data.data,
      })
    );
  } catch (err) {
    yield showEventError(err);
  }
}

export default function* EventsSagas() {
  yield takeEvery(types.GET_EVENTS_REQUEST, getEventsListSaga);
  yield takeEvery(types.GET_EVENT_REQUEST, getEventSaga);
  yield takeEvery(types.ADD_EVENT_REQUEST, addEventSaga);
  yield takeEvery(types.EDIT_EVENT_REQUEST, editEventSaga);
  yield takeEvery(types.PUBLISH_EVENT_REQUEST, publishEventSaga);
  yield takeEvery(types.DELETE_EVENT_REQUEST, deleteEventSaga);
  yield takeEvery(types.CANCEL_EVENT_REQUEST, cancelEventSaga);
  yield takeEvery(types.INVITE_APPLICANTS_REQUEST, inviteApplicantsSaga);
  yield takeEvery(types.SEND_NOTIFICATION_REQUEST, sendNotificationRequestSage);
  yield takeEvery(
    types.ADD_BULK_REWARD_TOGGLE_REQUEST,
    addBulkRewardToggleRequestSaga
  );
  yield takeEvery(types.GET_PAYROLL_PER_DAY_REQUEST, getPayrollPerDaySaga);
}
